import dayjs from 'dayjs';

import { TiendaPerfectaVisitHistoryDay } from 'models/TiendaPerfecta';

import { compliancePct } from './compliance';
import { LegendItem, MaterialGroup } from './materialCharts';
import {
  AMBER,
  CORAL,
  NAVY,
  SAGE,
  SLATE_DARK,
  STEEL_GRAY,
  TEAL,
  WHITE,
} from './palette';

/** Corporate Navy: executed visits, page and card titles. */
export { NAVY };

/**
 * Fixed semantic colors of the visit states (corporate palette). They never
 * come from `colorPrimary` (it changes per company) nor from the route
 * report: this view owns its values.
 */
export const VISIT_COLORS = {
  executed: NAVY,
  incident: AMBER,
  pending: STEEL_GRAY,
} as const;

/** Number printed on top of each state: white, except Slate Dark on Amber. */
export const VISIT_LABEL_COLORS = {
  executed: WHITE,
  incident: SLATE_DARK,
  pending: WHITE,
} as const;

/** Text-safe shades of the state colors (Amber is too light for text). */
export const VISIT_TEXT_COLORS = {
  executed: NAVY,
  incident: '#b45309',
  pending: STEEL_GRAY,
} as const;

/** Text of the material column in the consolidated grid. */
export const MATERIAL_TEXT_COLOR = '#1d4ed8';

/** Compliance line of the day chart (Teal, "progress and success"). */
export const LINE_COLOR = TEAL;

/** Compliance cuts: Coral / Amber / Sage. */
export const COMPLIANCE_COLORS = {
  bad: CORAL,
  warn: AMBER,
  ok: SAGE,
} as const;

/** 700 shades of the cuts, for text and for pills with white text. */
export const COMPLIANCE_LABEL_COLORS = {
  bad: '#be123c',
  warn: '#b45309',
  ok: '#047857',
} as const;

const NICE_STEPS = [1, 2, 4, 5, 8, 10];

/** Smallest "round" axis maximum (1, 2, 4, 5, 8 x 10^k) that fits `value`. */
export const niceMax = (value: number): number => {
  if (value <= 0) return 4;
  const pow = 10 ** Math.floor(Math.log10(value));
  const step = NICE_STEPS.find((s) => s * pow >= value) ?? 10;

  return step * pow;
};

export interface DayPoint {
  date: string;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  routes: number;
  /** `null` on days without routes: the line has no point there. */
  compliancePct: number | null;
  empty: boolean;
}

/**
 * Axis maximum of the "visits by day" chart: the tallest bar of any day, where
 * a bar is the stack (executed + incidents + pending) or the planned count if
 * that is larger, so no column can clip.
 */
export const dayAxisMax = (
  points: Pick<DayPoint, 'planned' | 'executed' | 'incidents' | 'pending'>[]
): number =>
  niceMax(
    Math.max(
      0,
      ...points.map((p) =>
        Math.max(p.planned, p.executed + p.incidents + p.pending)
      )
    )
  );

/** One point per day of the range; days the server omitted are empty. */
export const buildDayPoints = (
  days: string[],
  byDay: TiendaPerfectaVisitHistoryDay[]
): DayPoint[] => {
  const byDate = new Map(byDay.map((d) => [d.date, d]));

  return days.map((date) => {
    const day = byDate.get(date);
    // Empty only when nothing was planned: `routes` may be 0 or missing on a
    // day that still has visits, and those must render.
    if (!day || day.planned === 0) {
      return {
        date,
        planned: 0,
        executed: 0,
        incidents: 0,
        pending: 0,
        routes: 0,
        compliancePct: null,
        empty: true,
      };
    }

    return {
      ...day,
      routes: day.routes ?? 0,
      compliancePct: compliancePct(day.executed, day.planned),
      empty: false,
    };
  });
};

export interface MaterialDay {
  date: string;
  empty: boolean;
  total: number;
  parts: MaterialGroup['parts'];
}

/** Every day of the range with its material; days without routes are empty. */
export const buildMaterialDays = (
  points: DayPoint[],
  groups: MaterialGroup[]
): MaterialDay[] => {
  const byKey = new Map(groups.map((g) => [g.key, g]));

  return points.map((p) => {
    const group = byKey.get(p.date);

    return {
      date: p.date,
      empty: p.empty,
      total: group?.total ?? 0,
      parts: group?.parts ?? [],
    };
  });
};

export interface StackRow {
  date: string;
  material: string;
  value: number;
}

/** Flat rows for a stacked column chart (one per day and legend item). */
export const toStackRows = (
  days: MaterialDay[],
  legend: LegendItem[]
): StackRow[] => {
  const names = new Map(legend.map((l) => [l.materialId, l.name]));

  return days.flatMap((day) =>
    day.parts.map((part) => ({
      date: day.date,
      material: names.get(part.materialId) ?? part.materialId,
      value: part.units,
    }))
  );
};

/**
 * Axis label of a day: weekday and number (only the number on long ranges),
 * capitalized. Locale comes from the caller so it stays a pure function.
 */
export const dayTickLabel = (
  date: string,
  language: string,
  dense: boolean
): string => {
  const label = dayjs(date)
    .locale(language.startsWith('es') ? 'es' : 'en')
    .format(dense ? 'D' : 'ddd D');

  return label.charAt(0).toUpperCase() + label.slice(1);
};
